import React from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  TextInput,
} from 'react-native';
import Card from '../common/Card';
import Avatar from '../common/Avatar';
import PlatformIcon from '../common/PlatformIcon';

export interface AddFriendSearchResult {
  id: string;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  username?: string | null;
  contact?: string | null;
  avatarUrl?: string | null;
  bio?: string | null;
}

export interface AddFriendModalStrings {
  title: string;
  contactLabel: string;
  contactPlaceholder: string;
  searchHint: string;
  searching: string;
  searchResults: string;
  messageLabel: string;
  messagePlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  unknownUser: string;
}

export interface AddFriendModalProps {
  visible: boolean;
  searchQuery: string;
  friendMessage: string;
  searchResults: AddFriendSearchResult[];
  strings: AddFriendModalStrings;
  searchLoading?: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSearchQueryChange: (value: string) => void;
  onSubmitManual: () => void;
  onSelectSearchResult: (user: AddFriendSearchResult) => void;
  onFriendMessageChange: (value: string) => void;
}

const AddFriendModal: React.FC<AddFriendModalProps> = ({
  visible,
  searchQuery,
  friendMessage,
  searchResults,
  strings,
  searchLoading = false,
  submitting = false,
  onClose,
  onSearchQueryChange,
  onSubmitManual,
  onSelectSearchResult,
  onFriendMessageChange,
}) => {
  const confirmDisabled = submitting || !searchQuery.trim();

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalContainer}>
        <Card style={styles.modalCard}>
          <Text style={styles.modalTitle}>{strings.title}</Text>

          <View style={styles.fieldGroup}>
            <Text style={styles.fieldLabel}>{strings.contactLabel}</Text>
            <TextInput
              value={searchQuery}
              onChangeText={onSearchQueryChange}
              placeholder={strings.contactPlaceholder}
              style={styles.textInput}
              placeholderTextColor="#94a3b8"
              autoCapitalize="none"
              keyboardType="default"
            />
          </View>

          {!!searchQuery && (
            <View style={styles.searchHint}>
              <Text style={styles.searchHintText}>{strings.searchHint}</Text>
            </View>
          )}

          {searchLoading && (
            <View style={styles.searchingContainer}>
              <ActivityIndicator size="small" color="#2563eb" />
              <Text style={styles.searchingText}>{strings.searching}</Text>
            </View>
          )}

          {searchResults.length > 0 && (
            <View style={styles.searchResults}>
              <Text style={styles.resultsTitle}>{strings.searchResults}</Text>
              <ScrollView style={styles.resultsList}>
                {searchResults.map((user) => (
                  <TouchableOpacity
                    key={user.id}
                    style={styles.userResult}
                    onPress={() => onSelectSearchResult(user)}
                    accessibilityRole="button"
                  >
                    <Avatar
                      size="medium"
                      avatarUrl={user.avatarUrl || undefined}
                      displayName={user.displayName || strings.unknownUser}
                    />
                    <View style={styles.userInfo}>
                      <Text style={styles.userName} numberOfLines={1}>
                        {user.displayName || strings.unknownUser}
                      </Text>
                      {!!user.contact && (
                        <Text style={styles.userContact} numberOfLines={1}>
                          {user.contact}
                        </Text>
                      )}
                      {!!user.bio && (
                        <Text style={styles.userBio} numberOfLines={1}>
                          {user.bio}
                        </Text>
                      )}
                    </View>
                    <PlatformIcon name="add" size={20} color="#2563eb" />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}

          <View style={styles.fieldGroup}>
            <Text style={styles.fieldLabel}>{strings.messageLabel}</Text>
            <TextInput
              value={friendMessage}
              onChangeText={onFriendMessageChange}
              placeholder={strings.messagePlaceholder}
              style={[styles.textInput, styles.messageInput]}
              placeholderTextColor="#94a3b8"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.modalButton, styles.confirmButton, confirmDisabled && styles.disabledButton]}
              onPress={onSubmitManual}
              disabled={confirmDisabled}
              accessibilityRole="button"
            >
              <PlatformIcon name="check" size={20} color="#ffffff" />
              <Text style={styles.modalButtonText}>{strings.confirmLabel}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={onClose}
              accessibilityRole="button"
            >
              <PlatformIcon name="close" size={20} color="#475569" />
              <Text style={[styles.modalButtonText, styles.cancelButtonText]}>{strings.cancelLabel}</Text>
            </TouchableOpacity>
          </View>
        </Card>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
    padding: 16,
    justifyContent: 'center',
  },
  modalCard: {
    padding: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 16,
  },
  fieldGroup: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 14,
    color: '#475569',
    marginBottom: 6,
    fontWeight: '500',
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
  },
  messageInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  searchHint: {
    backgroundColor: '#eff6ff',
    padding: 10,
    borderRadius: 12,
    marginBottom: 12,
  },
  searchHintText: {
    fontSize: 13,
    color: '#1d4ed8',
  },
  searchingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 12,
    padding: 10,
    marginBottom: 12,
  },
  searchingText: {
    fontSize: 13,
    color: '#475569',
    marginLeft: 8,
  },
  searchResults: {
    maxHeight: 220,
    marginBottom: 16,
  },
  resultsTitle: {
    fontSize: 14,
    color: '#475569',
    fontWeight: '500',
    marginBottom: 8,
  },
  resultsList: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    backgroundColor: '#ffffff',
  },
  userResult: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  userInfo: {
    flex: 1,
    marginLeft: 12,
  },
  userName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#1f2937',
  },
  userContact: {
    fontSize: 13,
    color: '#64748b',
    marginTop: 2,
  },
  userBio: {
    fontSize: 13,
    color: '#94a3b8',
    marginTop: 2,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  modalButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
  },
  confirmButton: {
    backgroundColor: '#2563eb',
    marginRight: 8,
  },
  cancelButton: {
    backgroundColor: '#e2e8f0',
    marginLeft: 8,
  },
  disabledButton: {
    opacity: 0.5,
  },
  modalButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
  cancelButtonText: {
    color: '#1f2937',
  },
});

export default AddFriendModal;
